import { Icon } from "@iconify/react";
import { Colors } from "../../utils/colors";
import PopupModalContainer from "../Containers/PopupModalContainer";
import RectangleButton from "../Buttons/RectangleButton";
import { useCallback, useContext, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  LoginState,
  accountActions,
  getAccountState,
} from "../../redux/slices/accountSlice";
import { MixpanelContext } from "../../context/AnalyticsService";
import {
  connectToStripeAccount,
  testStripe,
} from "../../utils/stripeAccountUtils";
import { HorizontalDivider } from "../Dividers/HorizontalDivider";
import { StripeAccountStatus } from "@markit/common.types";
import { ThemeStyle } from "../../hooks/useTheme";
import { useLocation } from "react-router-dom";
import { useNavigate } from "../../hooks/useNavigate";
import { NavigationId } from "../../navigation/AppParamList";
import { MARKIT_DOCS } from "@markit/common.utils";
import { CopyableLinkDisplay } from "../Links/CopyCustomLinkify";

type EnablePaidTicketsModalProps = {
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
  isPromoter?: boolean;
  theme?: ThemeStyle;
};

const EnablePaidTicketsModal = (props: EnablePaidTicketsModalProps) => {
  const { isVisible, setIsVisible, isPromoter, theme } = props;
  const { accountData, loggedIn } = useSelector(getAccountState).account;
  const mixpanel = useContext(MixpanelContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [loading, setLoading] = useState(false);
  const [stripeConnectUrl, setStripeConnectUrl] = useState("");

  const styles = {
    subtext: {
      fontSize: 14,
      fontWeight: "400",
      color: Colors.GRAY1,
    },
  };

  const userStripeAccountStatus = useMemo(
    () =>
      testStripe(accountData.uid)
        ? accountData.testStripeAccountStatus || StripeAccountStatus.INCOMPLETE
        : accountData.stripeAccountStatus,
    [
      accountData.uid,
      accountData.stripeAccountStatus,
      accountData.testStripeAccountStatus,
    ]
  );

  const userStripeAccountId = useMemo(
    () =>
      testStripe(accountData.uid)
        ? accountData.testStripeAccountId
        : accountData.stripeAccountId,
    [
      accountData.uid,
      accountData.stripeAccountId,
      accountData.testStripeAccountId,
    ]
  );

  const connectToStripe = useCallback(async () => {
    if (loggedIn !== LoginState.LOGGED_IN) {
      dispatch(accountActions.setRedirectPath(pathname));
      navigate(NavigationId.WELCOME);
    } else if (userStripeAccountStatus === StripeAccountStatus.INCOMPLETE) {
      setLoading(true);
      mixpanel.track("Navigated To Set Up Paid Tickets", {
        origin: "profile",
      });
      const url = await connectToStripeAccount(accountData.uid);
      setStripeConnectUrl(url);
      setLoading(false);
    } else {
      setIsVisible(false);
    }
  }, [
    accountData.uid,
    userStripeAccountStatus,
    dispatch,
    loggedIn,
    mixpanel,
    navigate,
    pathname,
    setIsVisible,
  ]);

  const promoterSubtext =
    "The owner of this event has assigned you as a promoter. In order to receive revenue payouts and your promoter link, you must first connect or create a Stripe account.";

  const toggleModal = useCallback(() => {
    setIsVisible(!isVisible);
  }, [isVisible, setIsVisible]);

  return (
    <div>
      {isVisible ? (
        <PopupModalContainer
          headerComp={
            <div className="ColumnNormal" style={{ gap: 7 }}>
              <span
                style={{ fontSize: 16, fontWeight: "500", marginRight: 24 }}
              >
                {userStripeAccountStatus === StripeAccountStatus.INCOMPLETE
                  ? isPromoter
                    ? `${
                        userStripeAccountId ? "Finish connecting" : "Connect"
                      } to Stripe to receive payouts and share promoter link`
                    : `${
                        userStripeAccountId
                          ? "Finish Connecting Stripe"
                          : "Enable Payments on Markit"
                      }`
                  : userStripeAccountStatus ===
                      StripeAccountStatus.MANUAL_REVIEW
                    ? "Awaiting Manual Review"
                    : "Stripe Account Connected!"}
              </span>
              {userStripeAccountStatus === StripeAccountStatus.COMPLETED ? (
                <span style={styles.subtext}>
                  Your Stripe account is connected and ready to use.
                </span>
              ) : userStripeAccountStatus ===
                StripeAccountStatus.MANUAL_REVIEW ? (
                <span style={{ ...styles.subtext, paddingBottom: 14 }}>
                  Your new Markit Stripe account is being reviewed by our team.
                  We will approve your account within 12 hours (usually much
                  sooner). If you have questions or need this expedited for any
                  reason,{" "}
                  <a
                    href={MARKIT_DOCS + "/contactsupport"}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: Colors.BLACK }}
                  >
                    please contact our support team.
                  </a>
                </span>
              ) : userStripeAccountId ? (
                <span style={styles.subtext}>
                  {isPromoter
                    ? promoterSubtext
                    : "In order to receive payments on Markit you must finish connecting to your Stripe account."}
                </span>
              ) : (
                <span style={styles.subtext}>
                  {isPromoter
                    ? promoterSubtext
                    : "To receive payments on Markit you must connect or create a Stripe account."}
                </span>
              )}
            </div>
          }
          valueComp={
            userStripeAccountStatus ===
            StripeAccountStatus.MANUAL_REVIEW ? null : (
              <div
                className="AlignedColumn"
                style={{
                  paddingTop: 14,
                  paddingBottom: 24,
                  paddingInline: 34,
                  gap: 14,
                }}
              >
                <Icon
                  icon={"fa:cc-stripe"}
                  height={49}
                  style={{ color: theme?.ButtonBG.backgroundColor }}
                />
                <span style={{ ...styles.subtext, textAlign: "center" }}>
                  We use stripe to allow you to process purchases and manage
                  your payouts and funds.
                </span>
                <span style={{ ...styles.subtext, textAlign: "center" }}>
                  Once you have set up a Stripe account, all transactions will
                  flow through Stripe with payouts coming within 48 hours after
                  initiating.
                </span>
                <span style={{ ...styles.subtext, textAlign: "center" }}>
                  Using Stripe, you can directly connect with your debit card or
                  directly with your bank account.
                </span>
              </div>
            )
          }
          noDivider={
            userStripeAccountStatus === StripeAccountStatus.MANUAL_REVIEW
          }
          fixedBottomContent={
            <div className="ColumnNormal" style={{ gap: 14 }}>
              <HorizontalDivider />
              {stripeConnectUrl ? (
                <div
                  className="ColumnNormal"
                  style={{ gap: 4, paddingInline: 14 }}
                >
                  <span style={styles.subtext}>
                    Didn&apos;t open in a new tab? Copy the link below.
                  </span>
                  <CopyableLinkDisplay
                    copyText={stripeConnectUrl}
                    containerStyles={{
                      padding: 10,
                      backgroundColor: Colors.GRAY6,
                      border: `0.5px solid ${Colors.GRAY1}`,
                    }}
                  />
                </div>
              ) : null}
              <div style={{ paddingInline: 14 }}>
                <RectangleButton
                  buttonLabel={
                    <span>
                      {loggedIn !== LoginState.LOGGED_IN
                        ? "Login or Sign Up to Connect Stripe"
                        : userStripeAccountStatus ===
                            StripeAccountStatus.INCOMPLETE
                          ? userStripeAccountId
                            ? "Finish Connecting Stripe"
                            : "Connect Stripe"
                          : "Done"}
                    </span>
                  }
                  loading={loading}
                  disabled={loading}
                  onPress={connectToStripe}
                  altTextColor={theme ? theme.ButtonText.color : Colors.WHITE}
                  altColor={
                    theme ? theme.ButtonBG.backgroundColor : Colors.BLACK
                  }
                  containerStyles={{ paddingInline: 14, paddingBlock: 16 }}
                />
              </div>
            </div>
          }
          noExit
          closeModal={toggleModal}
          closeOnOutsidePress
          theme={theme}
        />
      ) : null}
    </div>
  );
};

export default EnablePaidTicketsModal;
